import { useProject } from "../../../../hooks/query";
import { AddRounded } from "@mui/icons-material";
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    Typography,
    IconButton as JoyIconButton,
    Dropdown,
    MenuButton,
    Menu,
    MenuItem,
} from "@mui/joy";
import { PackageCheck, ChevronDown, BookX, ListTodo, Building2 } from "lucide-react";
import { useParams } from "react-router-dom";

import { Skeleton, Stack } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";

import { Alert } from "../../../../shared";
import { useResponsive } from "../../../../hooks";

export function SummaryCard() {
    const params = useParams();
    const id = params.id as string;

    const { isMobile } = useResponsive();

    const { useGetProjectById } = useProject();
    const getProjectByIdSvc = useGetProjectById(id);

    const projectDetails = getProjectByIdSvc.data;
    const isLoading = getProjectByIdSvc.isPending;

    if (getProjectByIdSvc.isError) {
        return <Alert errorMessage={(getProjectByIdSvc.error as any)?.message || "Failed to load project summary"} />;
    }

    return (
        <AnimatePresence mode="wait">
            {isLoading ? (
                <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box display={"flex"} gap={1.5} alignItems={"center"}>
                            <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: "12px" }} />
                            <Stack spacing={0.5}>
                                <Skeleton variant="text" width={60} height={12} />
                                <Skeleton variant="text" width={220} height={28} />
                            </Stack>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
                            <Skeleton variant="rectangular" width="40%" height={36} sx={{ borderRadius: "8px" }} />
                            <Skeleton variant="rectangular" width="60%" height={36} sx={{ borderRadius: "8px" }} />
                        </Box>
                    </Box>
                </motion.div>
            ) : (
                <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: { xs: "flex-start", md: "center" },
                                justifyContent: "space-between",
                                flexDirection: { xs: "column", md: "row" },
                            }}
                        >
                            <Box display={"flex"} gap={1.5} alignItems={"center"}>
                                <Card sx={{ p: 1.5, borderRadius: "12px" }} variant="solid" color="primary">
                                    <Building2 size={24} strokeWidth={1.5} />
                                </Card>
                                <Box>
                                    <Typography
                                        level={"body-xs"}
                                        sx={{
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",
                                            fontWeight: 600,
                                            opacity: 0.6,
                                        }}
                                    >
                                        Project
                                    </Typography>
                                    <Typography level={"title-lg"} sx={{ fontWeight: 700 }}>
                                        {projectDetails?.Name || "-"}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "center",
                                    width: { xs: "100%", md: "auto" },
                                    justifyContent: { xs: "space-between", md: "flex-end" },
                                }}
                            >
                                {isMobile ? (
                                    <JoyIconButton
                                        variant="outlined"
                                        color="neutral"
                                        size="sm"
                                        sx={{ borderRadius: "8px" }}
                                    >
                                        <AddRounded />
                                    </JoyIconButton>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        startDecorator={<AddRounded />}
                                        size="sm"
                                        sx={{
                                            borderRadius: "8px",
                                            flex: { xs: 1, md: "initial" },
                                        }}
                                    >
                                        Follow
                                    </Button>
                                )}
                                <Dropdown>
                                    <ButtonGroup
                                        variant="outlined"
                                        color="danger"
                                        size="sm"
                                        sx={{
                                            borderRadius: "8px",
                                            flex: { xs: 2, md: "initial" },
                                        }}
                                    >
                                        <Button startDecorator={<PackageCheck size={18} />} sx={{ flex: 1 }}>
                                            Mark as complete
                                        </Button>
                                        <MenuButton slots={{ root: JoyIconButton }}>
                                            <ChevronDown size={18} />
                                        </MenuButton>
                                    </ButtonGroup>
                                    <Menu
                                        placement="bottom-end"
                                        sx={{ width: 210, borderRadius: "10px", zIndex: 10001 }}
                                    >
                                        <MenuItem>
                                            <BookX size={18} />
                                            Mark as cancelled
                                        </MenuItem>
                                        <MenuItem>
                                            <ListTodo size={18} />
                                            Mark as incomplete
                                        </MenuItem>
                                    </Menu>
                                </Dropdown>
                            </Box>
                        </Box>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
